import { axios } from '@/plugins/axios';
import merge from 'lodash/merge';
import Pager from '@/helpers/Pager';
import { Pagination, Resource, Statement, Option } from '@/helpers/Interfaces';
import { Resource as FileResource } from '@/modules/core/files/services/StorageService';
import { DateTime } from 'luxon';
import { $t } from '@/plugins/localization';

/**
 * CompaniesService
 */
export default class CompaniesService
{
    /**
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/renters/companies', {
            params: merge({}, pager.data())
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(publicId: string): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/renters/companies/${publicId}`)).data;
    }

    /**
      * @param model FormModel
      *
      * @returns Promise<Resource<FormModel>>
      */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(`admin/renters/companies`, model)).data;
    }

    /**
      * @param publicId string
      * @param model FormModel
      *
      * @returns Promise<Statement>
      */
    public static async update(publicId: string, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/renters/companies/${publicId}`, model)).data;
    }

    /**
      * @param publicId string
      *
      * @returns Promise<Statement>
      */
    public static async remove(publicId: string): Promise<Statement>
    {
        return (await axios.delete(`admin/renters/companies/${publicId}`)).data as Statement;
    }

    /**
     * @param publicId string
     * @param resource FileResource
     *
     * @returns Promise<Statement>
     */
    public static async updateLogo(publicId: string, resource: FileResource): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/renters/companies/${publicId}/logo`, resource)).data;
    }

    /**
     * @param file File
     * @param container string
     * @param secured boolean
     *
     * @returns Promise<UploadResource>
     */
    public static async uploadLogo(file: File, container: string, payload: any = null, secured: boolean = false): Promise<UploadResource>
    {
        const data = new FormData();

        data.append('container', container);
        data.append('payload', JSON.stringify(payload));
        data.append('secured', secured ? "true" : "false");
        data.append('file', file);

        const result = await axios.post<UploadResource>('admin/renters/companies/uploadLogo', data);

        return result.data;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getAutocompleteList(search: string, limit: number = 5): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>('admin/renters/companies/autocomplete', {
            params: {
                search: search,
                limit: limit
            }
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<ListItemModel>
     */
    public static async fetchAutocompleteModel(publicId: string): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/renters/companies/autocomplete/${publicId}`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    publicId: string;
    dateCreatedUtc: DateTime;
    name: string;
    website: string;
    email: string;
    phoneNumber: string;
    address: string;
    city: string;
    postalCode: string;
    nip: string;
    logoUrl: string;
}

export interface FilterModel
{
}

export interface FormModel
{
    publicId: string;
    isDefault: boolean;
    logo?: FileResource;
    name: string;
    website: string;
    email: string;
    phonePrefix: string;
    phone: string;
    address1: string;
    address2: string;
    city: string;
    postalCode: string;
    country: string;
    state: string;
    nip: string;
    krs: string;
    regon: string;
    bankName: string;
    bankAccountNumber: string;
    companyType: string;
}

export interface CompanyModel extends ListItemModel {}

interface UploadedResource
{
    upload?: File;
}

export interface UploadResource extends UploadedResource
{
    id: number;
    resourceType: string;
    dateCreatedUtc: DateTime;
    dateModifiedUtc: DateTime;
    createdBy: string;
    modifiedBy: string;
    scope: string;
    folderId?: number;
    name: string;
    contentType: string;
    contentLength: number;
    url: string;
    isImage: boolean;
    isSecured: boolean;
    meta: Record<string, string>;
    content: Record<string, Content>;
    media: string[];
}

export interface Content
{
    name: string;
    lead: string;
    alt: string;
}

enum CompanyTypeEnum {
    Own = 'Own',
    Cleaning = 'Cleaning',
    Laundry = 'Laundry'
}

const CompanyTypeOptions = (): Option[] => ([
    { value: CompanyTypeEnum.Own, text: $t('[[[Firma własna]]]') },
    { value: CompanyTypeEnum.Cleaning, text: $t('[[[Firma sprzątająca]]]') },
    { value: CompanyTypeEnum.Laundry, text: $t('[[[Pralnia]]]') }
]);

const CompanyTypeName = (value: CompanyTypeEnum): string => CompanyTypeOptions().find(s => s.value === value)?.text || CompanyTypeEnum[value];

export {
    CompanyTypeEnum,
    CompanyTypeOptions,
    CompanyTypeName
};
